import { useState, useEffect } from 'react';
import { useLogAnalyticsRestoreCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { Disclaimer } from '../shared/Disclaimer';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import type { LogAnalyticsRestoreRequest } from '../../api/types';
import { FieldTooltip } from '../shared/FieldTooltip';

const RESTORE_MIN_GB = 2048;

export default function LogAnalyticsRestoreCalc() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [inputs, setInputs] = useState<LogAnalyticsRestoreRequest>({
    dataToRestoreGB: 100,
    restoreDurationDays: 1,
    numberOfRestoresPerMonth: 1,
  });

  const set = (partial: Partial<LogAnalyticsRestoreRequest>) =>
    setInputs((prev) => ({ ...prev, ...partial }));

  const { data, isLoading, isError } = useLogAnalyticsRestoreCalc(inputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'log-analytics', result_gbp: data.monthly_estimate })
    }
  }, [data?.monthly_estimate])
  const underMinimum = inputs.dataToRestoreGB < RESTORE_MIN_GB;

  const num = (v: string) => Math.max(0, v === '' ? 0 : Number(v));

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full">
      {/* ── Left: Controls ── */}
      <div className="flex flex-col gap-6">

        {/* Always-visible 2TB warning banner */}
        <div className="bg-[#a4262c]/10 border border-[#a4262c]/50 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-red-400 text-2xl shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
            <div>
              <p className="text-sm font-bold text-red-400 mb-2">2 TB MINIMUM BILLING</p>
              <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Azure bills a minimum of <strong>2 TB (2,048 GB)</strong> per restore operation,
                regardless of actual data size. Restoring 1 GB costs the same as restoring 2,048 GB.
              </p>
              {underMinimum && (
                <p className="mt-2 text-[11px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
                  Your request: {inputs.dataToRestoreGB} GB → Billed as: {RESTORE_MIN_GB} GB
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`bg-surface-container-high micro-gradient ghost-border rounded-lg p-6 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-red-400" style={{ fontVariationSettings: "'FILL' 1" }}>restore</span>
            Restore Configuration
          </h2>

          <div className="grid grid-cols-1 gap-6">
            {/* Data to restore */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <div>
                  <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Data to Restore (GB)<FieldTooltip content="Azure bills a minimum of 2 TB (2,048 GB) per restore regardless of actual data size. Restoring 1 GB costs the same as restoring 2,048 GB." /></label>
                  {underMinimum && (
                    <p className="text-[10px] text-red-400 mt-0.5">Below 2 TB minimum — will be billed as {RESTORE_MIN_GB} GB</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  value={inputs.dataToRestoreGB}
                  onChange={(e) => set({ dataToRestoreGB: Math.max(1, num(e.target.value)) })}
                  className={`bg-surface-container-lowest border p-3 text-sm font-semibold focus:outline-none text-on-surface w-full font-mono ${underMinimum ? 'border-red-500/60 focus:border-red-500' : 'border-outline-variant focus:border-primary'}`}
                />
                <span className="text-outline text-sm font-mono shrink-0">GB</span>
              </div>
              <div className="flex items-center justify-between text-[10px] font-mono">
                <span className="text-outline">Requested: {inputs.dataToRestoreGB} GB</span>
                <span className={underMinimum ? 'text-red-400 font-bold' : 'text-outline'}>
                  Billed: {Math.max(inputs.dataToRestoreGB, RESTORE_MIN_GB)} GB
                </span>
              </div>
            </div>

            {/* Restore duration */}
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Restore Duration (days)<FieldTooltip content="How long the restored table stays accessible. Minimum billing is 12 hours but rounded up to 1 day. Extend only if you need interactive query access for longer." /></label>
              <p className="text-[10px] text-outline/60">Minimum 12 hours. Billed per day (or fraction).</p>
              <input
                type="number"
                min={1}
                max={365}
                value={inputs.restoreDurationDays}
                onChange={(e) => set({ restoreDurationDays: Math.max(1, num(e.target.value)) })}
                className="bg-surface-container-lowest border border-outline-variant p-3 text-sm font-semibold focus:outline-none focus:border-primary text-on-surface w-full"
              />
            </div>

            {/* Restores per month */}
            <div className="flex flex-col gap-2">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider flex items-center">Number of Restores / Month<FieldTooltip content="Each restore is an independent operation with its own 2 TB minimum. If you need to query the same data repeatedly, keep the restore table open longer rather than re-restoring." /></label>
              <p className="text-[10px] text-outline/60">Each restore is an independent operation billed separately.</p>
              <input
                type="number"
                min={1}
                max={100}
                value={inputs.numberOfRestoresPerMonth}
                onChange={(e) => set({ numberOfRestoresPerMonth: Math.max(1, num(e.target.value)) })}
                className="bg-surface-container-lowest border border-outline-variant p-3 text-sm font-semibold focus:outline-none focus:border-primary text-on-surface w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: Results ── */}
      <div className="flex flex-col gap-6">
        <div className="sticky top-[72px] flex flex-col gap-6">

          {/* Main cost card */}
          <div className="bg-surface-container-high micro-gradient border-l-2 border-red-500 rounded-lg overflow-hidden">
            <div className="p-6">
              <h3 className="font-mono text-[11px] text-outline uppercase tracking-widest mb-2">Monthly Restore Cost</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl font-bold font-headline text-red-400">
                  {isLoading ? '...' : isError ? 'Error' : `£${data?.monthly_estimate.toFixed(2)}`}
                </span>
                <span className="text-outline text-sm">/mo</span>
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="ml-1 transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
            </div>
            <div className="bg-surface-container-lowest/50 p-6 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-on-surface-variant flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">receipt_long</span>
                Billing Summary
              </h4>
              {data ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">Requested data</span>
                    <span className="font-bold">{inputs.dataToRestoreGB} GB</span>
                  </div>
                  <div className={`flex justify-between items-center font-mono text-xs ${underMinimum ? 'text-red-400' : ''}`}>
                    <span className={underMinimum ? 'text-red-400' : 'text-outline'}>
                      Billed data {underMinimum ? '(2TB minimum applied)' : ''}
                    </span>
                    <span className="font-bold">{data.effective_billed_gb.toLocaleString()} GB</span>
                  </div>
                  <div className="flex justify-between items-center font-mono text-xs">
                    <span className="text-outline">Daily rate</span>
                    <span className="font-bold">£{data.daily_cost.toFixed(2)}/day</span>
                  </div>
                  <div className="pt-2 border-t border-outline-variant">
                    <div className="flex justify-between items-center font-mono text-xs">
                      <span className="text-outline">{inputs.restoreDurationDays} day{inputs.restoreDurationDays !== 1 ? 's' : ''} × {inputs.numberOfRestoresPerMonth} restore{inputs.numberOfRestoresPerMonth !== 1 ? 's' : ''}</span>
                      <span className="font-bold text-red-400 text-sm">£{data.monthly_estimate.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-[10px] text-outline text-center py-4">Adjust inputs to see pricing</p>
              )}
            </div>

            {/* Minimum warning inline */}
            {data?.restore_minimum_warning && (
              <div className="border-t border-[#a4262c]/30 bg-[#a4262c]/5 p-4">
                <p className="text-[11px] text-red-400 leading-relaxed flex items-start gap-2">
                  <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>warning</span>
                  {data.restore_minimum_warning}
                </p>
              </div>
            )}
          </div>

          {/* Search Job alternative — green box */}
          {data?.search_job_comparison && (
            <div className="bg-[#107c10]/10 border border-[#107c10]/40 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-[#107c10]" style={{ fontVariationSettings: "'FILL' 1" }}>search</span>
                <h4 className="text-sm font-bold text-[#107c10]">Cheaper Alternative: Search Job</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-outline">Restore (your estimate)</span>
                  <span className="text-sm font-mono font-bold text-red-400">£{data.monthly_estimate.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[11px] text-outline">Search Job (same data)</span>
                  <span className="text-sm font-mono font-bold text-[#107c10]">£{data.search_job_comparison.search_job_total.toFixed(2)}</span>
                </div>
                <div className="pt-2 border-t border-[#107c10]/30">
                  <p className="text-[11px] font-bold text-[#107c10]">
                    Save £{data.search_job_comparison.saving_vs_restore.toFixed(2)} by using Search Jobs instead
                  </p>
                </div>
                <p className="text-[10px] text-outline/70 leading-relaxed">{data.search_job_comparison.note}</p>
              </div>
            </div>
          )}

          {/* Recommendation */}
          {data?.recommendation && (
            <div className="bg-surface-container-high ghost-border rounded-lg p-4">
              <p className="text-[11px] text-outline leading-relaxed flex items-start gap-2">
                <span className="material-symbols-outlined text-primary text-[14px] shrink-0 mt-0.5">lightbulb</span>
                {data.recommendation}
              </p>
            </div>
          )}

          <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
            <Disclaimer />
          </div>
          <div className="pb-2">
            <ShareButton inputs={inputs} result={data ?? null} service="log-analytics" />
          </div>
          <ResourcesSection service="log-analytics" />
        </div>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="log-analytics/restore"
          estimateValue={data ? `£${data.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}
